import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../ui/card';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import {
  centsToUsd,
  formatUsdFromCents,
  yearlyMonthlyCents,
  slugToInternal,
  getCheckoutAddonLines,
  PLAN_TIER,
} from '../../../lib/pricingUtils';
import { startCatalogCheckout, previewProration } from '../../../lib/customerBillingApi';
import { createStripeCheckout } from '../../../lib/billingApi';

export default function PricingCheckoutPanel({
  selectedPlan,
  billingCycle,
  onBillingCycleChange,
  addons,
  selectedAddons,
  addonQuantities,
  subscription,
  internalPlanId,
  isTrialing,
  organizationId,
  paymentsEnabled,
  stripeEnabled,
  user,
  paying,
  setPaying,
  planAutoRenewOn = true,
  onPlanAutoRenewChange,
  actionLoading = false,
  renewalRequired = false,
}) {
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const selectedPlanId = selectedPlan?.id ?? null;
  const curTier = PLAN_TIER[internalPlanId] ?? 0;
  const tgtTier = PLAN_TIER[slugToInternal(selectedPlanId)] ?? 0;
  const onFreeOrTrial = isTrialing || !internalPlanId || internalPlanId === 'free';

  const needsPlanPayment =
    Boolean(selectedPlan) &&
    selectedPlan.id !== 'enterprise' &&
    // A lapsed plan has to be payable again, including at its own tier.
    (onFreeOrTrial || renewalRequired || tgtTier > curTier);

  // Nothing was paid for a lapsed period, so there is no proration to preview —
  // a renewal is always billed at the plan's full price.
  const isPlanUpgrade =
    needsPlanPayment && !renewalRequired && !onFreeOrTrial && tgtTier > curTier;

  const newAddonLines = () =>
    getCheckoutAddonLines(addons, selectedAddons, addonQuantities, subscription, billingCycle);

  const fullPlanCharge = () => {
    if (!needsPlanPayment || !selectedPlan) return 0;
    const cents =
      billingCycle === 'yearly'
        ? selectedPlan.yearly_price_cents
        : selectedPlan.monthly_price_cents;
    return centsToUsd(cents);
  };

  const planCharge = () => {
    if (!needsPlanPayment) return 0;
    if (isPlanUpgrade && upgradePreview?.amount_due_usd != null) {
      return Number(upgradePreview.amount_due_usd) || 0;
    }
    return fullPlanCharge();
  };

  const addonCharge = () =>
    newAddonLines().reduce((sum, line) => {
      if (line.amountCents != null) {
        return sum + centsToUsd(line.amountCents);
      }
      const unit = centsToUsd(line.cents);
      return sum + unit * line.quantity;
    }, 0);

  const total = planCharge() + addonCharge();
  const canPay = total > 0 && organizationId && user;
  // Don't treat a lapsed plan as an "active" subscription for auto-renew UI.
  const hasActivePlan = internalPlanId && internalPlanId !== 'free' && !isTrialing && !renewalRequired;

  useEffect(() => {
    let cancelled = false;
    if (!organizationId || !selectedPlanId || !isPlanUpgrade) {
      setUpgradePreview(null);
      return undefined;
    }
    setPreviewLoading(true);
    previewProration(organizationId, {
      planSlug: selectedPlanId,
      billingCycle,
    })
      .then((data) => {
        if (!cancelled) setUpgradePreview(data);
      })
      .catch(() => {
        if (!cancelled) setUpgradePreview(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, selectedPlanId, billingCycle, isPlanUpgrade]);

  const handlePay = async () => {
    if (!organizationId) {
      toast.error('Select a workspace first');
      return;
    }
    if (!user) {
      toast.error('Sign in to continue');
      return;
    }
    if (total <= 0) {
      toast.error('Select a plan or add-on to checkout');
      return;
    }
    setPaying(true);
    try {
      const addonLines = newAddonLines().map((l) => ({
        addonId: l.addonId,
        quantity: l.quantity,
      }));
      if (stripeEnabled && needsPlanPayment) {
        const { checkout_url } = await createStripeCheckout(
          organizationId,
          slugToInternal(selectedPlan.id),
          billingCycle
        );
        window.location.href = checkout_url;
        return;
      }
      if (paymentsEnabled) {
        const planPay = needsPlanPayment;
        const { pay_url, order_id } = await startCatalogCheckout(organizationId, {
          planSlug: planPay ? selectedPlan.id : null,
          billingCycle,
          addonLines,
          addonsOnly: !planPay,
        });
        if (pay_url) {
          const sep = pay_url.includes('?') ? '&' : '?';
          window.location.href = `${pay_url}${sep}return_order=${order_id || ''}`;
        }
        return;
      }
      toast.error('Payments are not configured');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <Card className="sticky top-6 border-[#2a5568] bg-[#214050] text-slate-100 shadow-xl shadow-black/20">
      <CardHeader>
        <CardTitle className="text-lg text-white">Checkout</CardTitle>
        <CardDescription className="text-slate-300/90">Review total and pay securely.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
            Billing cycle
          </p>
          <div className="inline-flex rounded-full border border-[#2a5568] bg-[#1a3542] p-0.5 w-full">
            <button
              type="button"
              className={`flex-1 px-3 py-1.5 rounded-full text-sm font-medium transition ${
                billingCycle === 'monthly'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
              onClick={() => onBillingCycleChange('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`flex-1 px-3 py-1.5 rounded-full text-sm font-medium transition ${
                billingCycle === 'yearly'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-slate-300 hover:text-white'
              }`}
              onClick={() => onBillingCycleChange('yearly')}
            >
              Yearly
            </button>
          </div>
        </div>

        {hasActivePlan && onPlanAutoRenewChange && (
          <div className="flex items-center justify-between gap-2 border-t border-[#2a5568] pt-3">
            <Label htmlFor="checkout-plan-ar" className="text-sm text-slate-300">
              Plan auto-renew
            </Label>
            <Switch
              id="checkout-plan-ar"
              checked={planAutoRenewOn}
              disabled={actionLoading}
              onCheckedChange={onPlanAutoRenewChange}
            />
          </div>
        )}

        <div className="space-y-2 text-sm border-t border-[#2a5568] pt-3">
          {needsPlanPayment && selectedPlan && (
            <div className="space-y-1">
              <div className="flex justify-between gap-2">
                <span className="text-slate-300">
                  {isPlanUpgrade
                    ? `Upgrade to ${selectedPlan.name}`
                    : renewalRequired
                      ? `Renew ${selectedPlan.name}`
                      : selectedPlan.name}
                </span>
                <span className="font-medium shrink-0 text-white">
                  {previewLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin inline" />
                  ) : isPlanUpgrade ? (
                    `$${planCharge().toFixed(2)}`
                  ) : billingCycle === 'yearly' ? (
                    `${formatUsdFromCents(yearlyMonthlyCents(selectedPlan.yearly_price_cents))}/mo`
                  ) : (
                    `${formatUsdFromCents(selectedPlan.monthly_price_cents)}/mo`
                  )}
                </span>
              </div>
              {isPlanUpgrade && (
                <p className="text-xs text-slate-400">
                  Plan difference due today. Next renewal at {selectedPlan.name} price (
                  {billingCycle === 'yearly'
                    ? formatUsdFromCents(selectedPlan.yearly_price_cents)
                    : formatUsdFromCents(selectedPlan.monthly_price_cents)}
                  /{billingCycle === 'yearly' ? 'yr' : 'mo'}).
                </p>
              )}
            </div>
          )}
          {newAddonLines().map((line) => (
            <div key={line.addonId} className="flex justify-between gap-2">
              <span className="text-slate-300">
                {line.name}
                {line.isQuantityBased
                  ? ` +${line.quantity}`
                  : line.quantity > 1
                    ? ` × ${line.quantity}`
                    : ''}
              </span>
              <span className="text-slate-100">
                ${(centsToUsd(line.cents) * line.quantity).toFixed(2)}
              </span>
            </div>
          ))}
          {!needsPlanPayment && !newAddonLines().length && (
            <p className="text-slate-400 text-sm">Select a plan or add-ons to checkout.</p>
          )}
        </div>

        <div className="flex justify-between font-semibold text-base border-t border-[#2a5568] pt-3 text-white">
          <span>Total due today</span>
          <span>${total.toFixed(2)}</span>
        </div>
        <p className="text-xs text-slate-400">
          Charged in QAR at checkout (USD × 3.78). Plan and add-ons renew separately each{' '}
          {billingCycle === 'yearly' ? 'year' : 'month'} unless you turn off auto-renew.
        </p>
      </CardContent>
      <CardFooter className="flex-col gap-3">
        <Button
          className="w-full bg-blue-500 hover:bg-blue-400 text-white"
          disabled={!canPay || paying || previewLoading}
          onClick={handlePay}
        >
          {paying && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Pay ${total.toFixed(2)}
        </Button>
        {!user && (
          <p className="text-xs text-center text-slate-400">Sign in to complete purchase</p>
        )}
      </CardFooter>
    </Card>
  );
}
